<template>
  <Toast />
  <component :is="layout">
    <RouterView />
  </component>
</template>

<script setup lang="ts">
import { App, type URLOpenListenerEvent } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Keyboard } from '@capacitor/keyboard'
import { Preferences } from '@capacitor/preferences'
import { StatusBar, Style } from '@capacitor/status-bar'
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'
import { Badge } from '@capawesome/capacitor-badge'
import { useLocalStorage } from '@vueuse/core'
import axios, { AxiosError, type AxiosResponse } from 'axios'
import { computed, defineComponent, onBeforeMount, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import Toast from '@/components/common/toast/Toast.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { LiveActivity } from '@/plugins/LiveActivityPlugin'
import { isTokenExpired, RefreshExpiredError } from '@/utils/jwt'

import { useActivityTracker } from './composables/useActivityTracker'
import { useToast } from './composables/useToast'
import { useUserRoles } from './composables/useUserRoles'
import { PLATFORMS, TOAST_TIME } from './constants/constants'
import { LOGIN } from './modules/auth/routes'
import { useAuthStore } from './modules/auth/store'
import { useCounterStore } from './modules/counter/store'
import { APP_STORE_ID, STORAGE_KEYS, TOKEN_REFRESH_LEEWAY_SECONDS } from './settings'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const { t } = useI18n()
const authStore = useAuthStore()
const counterStore = useCounterStore()
const layout = computed(
  (): ReturnType<typeof defineComponent> => route.meta.layout || DefaultLayout
)
const { isPatient } = useUserRoles()

// Setup activity tracker for foreground inactivity detection
const {
  setupListeners,
  removeListeners,
  resetActivityTime,
  checkMaxSessionTimeout,
  checkBackgroundTimeout,
} = useActivityTracker()

// Watch for user login/logout to enable/disable activity tracking
watch(
  () => authStore.user,
  (user) => {
    if (user) {
      setupListeners()
    } else {
      removeListeners()
    }
  }
)

// Helper function to handle error fields that can be string or string[]
const showErrorToasts = (errorField: string | string[] | undefined) => {
  if (!errorField) return

  const errors = Array.isArray(errorField) ? errorField : [errorField]
  errors.forEach((error) => {
    toast.add({
      severity: 'error',
      detail: error,
      ttl: TOAST_TIME,
    })
  })
}

onBeforeMount(async () => {
  // Variables for request queue during token refresh
  let failedQueue: Array<{ resolve: (value?: any) => void; reject: (reason?: any) => void }> = []

  const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error)
      } else {
        promise.resolve(token)
      }
    })
    failedQueue = []
  }

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (e: AxiosError<{ detail?: string | string[] }>) => {
      const status = e?.response?.status
      const data: any = e?.response?.data
      const originalRequest: any = e.config

      const message =
        data?.message ||
        data?.detail ||
        data?.error ||
        data?.non_field_errors ||
        (status === 403 ? t('system.access-denied') : null)

      if (status === 401) {
        const url = e?.request?.responseURL || ''

        // Avoid infinite loops on logout/refresh
        if (url.includes('logout') || url.includes('refresh')) {
          throw e
        }

        // If already logging out - silently resolve without showing error
        // This prevents race condition errors when logout is triggered by timeout
        // and parallel API requests fail with 401
        // Return resolved promise to avoid unhandled rejection since logout is already in progress
        if (authStore.isLoggingOut) {
          console.log('[Auth] Already logging out, suppressing 401 error')
          return Promise.resolve(e as any)
        }

        // This prevents infinite retry loops
        // _retried flag marks a request that was already retried after token refresh
        // If it fails again with 401, it means refresh didn't help or token is still invalid
        // Without this check, interceptor would try to refresh again → infinite loop
        if (originalRequest._retried) {
          console.log('[Auth] Request already retried after refresh, giving up')
          // Only show error if not logging out (to avoid double errors)
          if (!authStore.isLoggingOut) {
            showErrorToasts(message)
          }
          await authStore.logout(true)
          await router.push({ name: LOGIN, query: { redirect_to: route.query.redirect_to } })
          // Return resolved promise to avoid unhandled rejection since logout and redirect are done
          return Promise.resolve(e as any)
        }

        // If refresh is already in progress - add request to queue
        if (authStore.isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((token) => {
              // Update Authorization header with the newly refreshed access token
              // This ensures the retried request will be authenticated with the fresh token
              // instead of the expired one that caused the original 401 error
              originalRequest.headers = {
                ...(originalRequest.headers || {}),
                Authorization: 'Bearer ' + token,
              }
              // Mark request as retried to prevent infinite refresh loops
              originalRequest._retried = true
              return axios(originalRequest)
            })
            .catch((err) => {
              throw err
            })
        }

        // Start refresh process
        // Mark request as retried BEFORE refresh to prevent infinite loops
        // If refresh succeeds but retry still fails with 401, we'll logout instead of retrying refresh
        originalRequest._retried = true

        try {
          // Try to refresh token with correct type (regular or long-lived)
          // Use forceRefresh=true to skip time check and refresh immediately
          // since we already know token is invalid (got 401)
          const newAccessToken = await authStore.getAccessToken(true)

          // Process queued requests
          processQueue(null, newAccessToken)

          // Retry original request
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            Authorization: 'Bearer ' + newAccessToken,
          }
          return axios(originalRequest)
        } catch (refreshError) {
          // Refresh failed - logout as before
          processQueue(refreshError, null)

          // Check if refresh token expired
          if (refreshError instanceof RefreshExpiredError) {
            console.log('[Auth] Refresh token expired, logging out')
            toast.add({
              severity: 'info',
              detail: t('auth.refresh-token-expired'),
              ttl: TOAST_TIME,
            })
          } else {
            // Only show error if not already logging out (to avoid double errors)
            if (!authStore.isLoggingOut) {
              showErrorToasts(message)
            }
          }

          await authStore.logout(true)
          await router.push({ name: LOGIN, query: { redirect_to: route.query.redirect_to } })
          // Return resolved promise to avoid unhandled rejection since logout and redirect are done
          return Promise.resolve(refreshError as any)
        }
      }

      // For all other errors
      if (message) {
        showErrorToasts(message)
      }
      throw e
    }
  )

  await App.removeAllListeners()

  App.addListener('appUrlOpen', function (event: URLOpenListenerEvent) {
    // Example url: https://beerswift.ch/tabs/tabs2
    // slug = /tabs/tabs2
    const slug = event.url
      .split('.ch')
      .pop()
      ?.replace(/\/\//g, '/') // workaround for double slash
      .replace(/\/\?/g, '?') // workaround for slash before query params to handle proper title
      .replace(/\/$/, '') // workaround for trailing slash
    if (slug) {
      const [path, queryParams] = slug.split('?')
      const query = queryParams
        ?.split('&')
        .map((param) => param.split('='))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      router.push({
        path,
        query,
      })
    }
  })

  App.addListener('appStateChange', async ({ isActive }) => {
    if (isActive) {
      // CRITICAL: Wrap checkUpdate in try-catch to prevent it from blocking security checks
      try {
        await checkUpdate()
      } catch (error) {
        console.warn('[AppUpdate] Failed to check for updates:', error)
        // Continue with security checks anyway
      }

      const now = Date.now()
      console.log('[AppState] 🟢 Returned to FOREGROUND at:', new Date(now).toISOString())

      // Read from Preferences API (more reliable than localStorage on iOS)
      const { value: last } = await Preferences.get({ key: STORAGE_KEYS.LAST_ACTIVE })

      if (!authStore.user) {
        console.log('[AppState] ⚠️ No user logged in, skipping checks')
        return
      }

      if (!last) {
        console.log('[AppState] ⚠️ No lastActive found - first launch or data lost')
        // Reset activity time to ensure foreground timeout starts fresh
        resetActivityTime()
        return
      }

      console.log('[AppState] Last active timestamp:', last)
      const timeSinceLastActive = now - +last
      console.log(
        '[AppState] Time in background:',
        Math.round(timeSinceLastActive / 1000),
        'seconds'
      )
      // Security check 1: Maximum session time (MAX_SESSION_TIME)
      const maxSessionExceeded = await checkMaxSessionTimeout()
      if (maxSessionExceeded) {
        return
      }

      // Security check 2: Background inactivity timeout (BACKGROUND_INACTIVITY_TIMEOUT)
      const backgroundTimeoutExceeded = await checkBackgroundTimeout(timeSinceLastActive)
      if (backgroundTimeoutExceeded) {
        // Remove lastActive and return - logout will happen
        await Preferences.remove({ key: STORAGE_KEYS.LAST_ACTIVE })
        return
      }

      // Background timeout not exceeded - remove lastActive and reset activity time
      // This ensures foreground timeout won't trigger immediately after returning from background
      await Preferences.remove({ key: STORAGE_KEYS.LAST_ACTIVE })
      resetActivityTime()

      // Proactive check: Verify refresh token expiration before making any API requests
      // This check runs when app returns from background (before first API call)
      //
      // Why here and not just in getAccessToken()?
      // - getAccessToken() already checks refresh token expiration, but only when called
      // - This proactive check prevents unnecessary API requests (e.g., counterStore.getCounter() below)
      // - Better UX: logout immediately instead of waiting for API request to fail
      // - With token rotation, refresh token expiration is rare, but this prevents edge cases
      //
      // Note: This is NOT critical - if removed, getAccessToken() + interceptor will handle it,
      // but this provides better UX by catching expired tokens before any network requests
      const refreshToken = authStore.getRefreshToken()
      if (refreshToken && isTokenExpired(refreshToken, TOKEN_REFRESH_LEEWAY_SECONDS)) {
        console.log('[Auth] Refresh token expired - logging out')
        await authStore.logout(true)
        router.push({
          name: LOGIN,
          query: { redirect_to: route.fullPath, reason: 'token_expired' },
        })
        return
      }

      if (isPatient.value) {
        counterStore.getCounter()
      }

      return
    }

    // On pause, only save last active time
    if (!authStore.user) {
      console.log('[AppState] No user logged in, skipping background save')
      return
    }
    const timestamp = Date.now()
    console.log('[AppState] 🔴 Going to BACKGROUND at:', new Date(timestamp).toISOString())
    console.log('[AppState] User:', authStore.user?.user?.email)
    await Preferences.set({ key: STORAGE_KEYS.LAST_ACTIVE, value: timestamp.toString() })
    console.log('[AppState] ✅ Saved lastActive:', timestamp)
  })
})

async function checkUpdate() {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  const result = await AppUpdate.getAppUpdateInfo({ country: 'CH' })

  console.log('[AppUpdate] Check result:', {
    current: result.currentVersionName,
    available: result.availableVersionName,
    updateAvailability: result.updateAvailability,
  })

  if (result.updateAvailability > AppUpdateAvailability.UPDATE_NOT_AVAILABLE) {
    console.log('[AppUpdate] Update available, opening app store')
    await AppUpdate.openAppStore({ appId: APP_STORE_ID })
  }
}

const fcmToken = computed(() => authStore.fcmToken)
const startLiveActivityToken = useLocalStorage(STORAGE_KEYS.START_LIVE_ACTIVITY_TOKEN, '', {
  mergeDefaults: true,
})

watch(
  [fcmToken, startLiveActivityToken],
  ([fcm, startToken]) => {
    console.log('[Live Activity] app watch token')
    if (fcm && startToken) {
      console.log('[Live Activity] Sending start token to server')
      authStore.sendStartLiveActivityToken(fcm, startToken)
    }
  },
  { immediate: true }
)

onMounted(async () => {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  console.log('[Live Activity] app on mounted live activity listener token setup')

  if (Capacitor.getPlatform() === PLATFORMS.IOS) {
    LiveActivity.addListener('startTokenReceived', async (token) => {
      console.log('[Live Activity] Start live activity token: ' + token.token)
      startLiveActivityToken.value = token.token
    })
  }

  await Keyboard.removeAllListeners()

  Keyboard.addListener('keyboardWillShow', (info) => {
    const dialog = document.querySelector('.dialog') as HTMLElement
    if (Capacitor.getPlatform() === PLATFORMS.IOS && dialog) {
      dialog.style.bottom = info.keyboardHeight + 'px'
    }
    const el = document.activeElement as HTMLElement
    if (el) el.scrollIntoView({ block: 'center' })
  })

  Keyboard.addListener('keyboardWillHide', () => {
    const dialog = document.querySelector('.dialog') as HTMLElement
    if (Capacitor.getPlatform() === PLATFORMS.IOS && dialog) {
      dialog.style.bottom = 0 + 'px'
    }
  })

  if (Capacitor.isNativePlatform() && (await Badge.isSupported())) {
    const { display } = await Badge.requestPermissions()
    counterStore.isBadgeSupported = display === 'granted'
  }

  await StatusBar.setStyle({ style: Style.Light })
  if (Capacitor.getPlatform() === PLATFORMS.ANDROID) {
    await StatusBar.setBackgroundColor({ color: '#ffffff' })
  }

  try {
    await checkUpdate()
  } catch (error) {
    console.warn('[AppUpdate] Failed to check for updates:', error)
  }
})
</script>
